import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import debounce from '../../utils/ids-debounce-utils/ids-debounce-utils';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { isObject } from '../../utils/ids-object-utils/ids-object-utils';

import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsLabelStateMixin from '../../mixins/ids-label-state-mixin/ids-label-state-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsValidationMixin from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';
import '../ids-button/ids-button';
import '../ids-text/ids-text';
import '../ids-checkbox/ids-checkbox';
import '../ids-dropdown/ids-dropdown';
import '../ids-input/ids-input';
import '../ids-message/ids-message';
import '../ids-modal/ids-modal';
import '../ids-toolbar/ids-toolbar';

import type IdsButton from '../ids-button/ids-button';
import type IdsLoadingIndicator from '../ids-loading-indicator/ids-loading-indicator';
import type IdsToolbarMoreActions from '../ids-toolbar/ids-toolbar-more-actions';
import type IdsText from '../ids-text/ids-text';

import {
  VIEWS,
  PARAGRAPH_SEPARATORS,
  EditorAction,
  CLASSES,
  EDITOR_DEFAULTS,
  EDITOR_ATTRIBUTES,
  qs,
  qsAll,
  rgbToHex,
  FONT_SIZE_ACTIONS,
  TEXT_FORMAT_ACTIONS
} from './ids-editor-shared';

import {
  cleanHtml,
  trimContent
} from './ids-editor-clean-utils';

import {
  blockElem,
  saveSelectionObject,
  restoreObjectSelection,
  selectionBlockElems,
  selectionParents,
  findElementInSelection,
  SelectionState
} from './ids-editor-selection-utils';

import {
  parseTemplate,
  editorTemplate,
  btnEditorModeTemplate,
  btnSourceModeTemplate,
  toolbarTemplate,
  errorMessageTemplate,
  hyperlinkModalTemplate,
  insertimageModalTemplate
} from './ids-editor-templates';

import {
  handlePasteAsPlainText,
  handlePasteAsHtml
} from './ids-editor-handle-paste';

import formatHtml from './ids-editor-formatters';

import styles from './ids-editor.scss';
import type IdsIcon from '../ids-icon/ids-icon';

export interface IdsEditorModals {
  /** The hyperlink options */
  hyperlink?: {
    /** Url for hyperlink */
    url?: string;
    /** Css Class for hyperlink */
    classes?: string;
    /** List target options for hyperlink */
    targets?: Array<{ text: string, value: string, selected?: boolean }>;
    /** If true, isClickable checkbox should checked */
    isClickable?: boolean;
    /** If true, will show isClickable checkbox */
    showIsClickable?: boolean;
  };
  /** The insertimage options */
  insertimage?: {
    /** Url for insertimage */
    url?: string;
    /** Alt text for insertimage */
    alt?: string;
  };
}

// Instance counter
let instanceCounter = 0;
const defaultHeight = '326px';

const Base = IdsValidationMixin(
  IdsLabelStateMixin(
    IdsDirtyTrackerMixin(
      IdsLocaleMixin(
        IdsEventsMixin(
          IdsElement
        )
      )
    )
  )
);

/**
 * IDS Editor Component
 * @type {IdsEditor}
 * @inherits IdsElement
 * @mixes IdsDirtyTrackerMixin
 * @mixes IdsEventsMixin
 * @mixes IdsLabelStateMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsValidationMixin
 * @part editor - the editor element
 * @part editor-label - the editor label element
 * @part main-container - the main container element
 * @part toolbar-container - the toolbar container element
 * @part editor-container - the editor container element
 * @part source-container - the source container element
 */
@customElement('ids-editor')
@scss(styles)
export default class IdsEditor extends Base {
  validationElems?: Record<string, any>;

  reqInitialize?: boolean;

  isFormComponent = true;

  #resizeObserver = new ResizeObserver(() => this.#resize());

  /**
   * Modals attached to editor.
   * @private
   */
  #modals: any = {};

  /**
   * Current paragraph separator.
   * @private
   */
  #paragraphSeparator?: string;

  /**
   * Saved current selection ranges.
   * @private
   */
  #savedSelection: SelectionState | null = null;

  /**
   * Cache elements use most.
   * @private
   */
  #elems: any = {};

  input?: any;

  labelEl?: IdsText | null;

  #editorTextContainer: HTMLElement | null = null;

  /**
   * List of actions can be execute with editor.
   * extra actions get added in `#initContent()`
   * @private
   */
  #actions: Record<string, EditorAction> = {
    // Styles
    bold: { action: 'bold', keyid: 'KeyB' },
    italic: { action: 'italic', keyid: 'KeyI' },
    underline: { action: 'underline', keyid: 'KeyU' },
    strikethrough: { action: 'strikeThrough', keyid: 'KeyS|shift' },

    // Scripts
    superscript: { action: 'superscript', keyid: 'Equal|shift' },
    subscript: { action: 'subscript', keyid: 'Equal' },

    // Text formats
    formatblock: { action: 'formatBlock' },
    ...TEXT_FORMAT_ACTIONS,

    // Font size
    fontsize: { action: 'fontSize' },
    ...FONT_SIZE_ACTIONS,

    // Colors
    forecolor: { action: 'forecolor', keyid: 'KeyK|shift|alt' },
    backcolor: { action: 'backcolor' },

    // Lists
    orderedlist: { action: 'insertOrderedList', keyid: 'KeyO|shift' },
    unorderedlist: { action: 'insertUnorderedList', keyid: 'KeyU|shift' },

    // Insert
    insertimage: { action: 'insertImage', keyid: 'KeyI|shift' },
    hyperlink: { action: 'createLink', keyid: 'KeyK' },
    unlink: { action: 'unlink', keyid: 'KeyK|shift' },
    inserthtml: { action: 'insertHTML' },
    inserthorizontalrule: { action: 'insertHorizontalRule', keyid: 'KeyL|shift' },

    // Alignment
    alignleft: { action: 'alignLeft', keyid: 'KeyL' },
    alignright: { action: 'alignRight', keyid: 'KeyR' },
    aligncenter: { action: 'alignCenter', keyid: 'KeyE' },
    alignjustify: { action: 'alignJustify', keyid: 'KeyJ' },

    // Clear formatting
    clearformatting: { action: 'removeFormat', keyid: 'Space|shift' },

    // History
    redo: { action: 'redo', keyid: 'KeyY' },
    undo: { action: 'undo', keyid: 'KeyZ' },

    // Extra
    editormode: { action: 'editorMode', keyid: 'Backquote|shift' },
    sourcemode: { action: 'sourceMode', keyid: 'Backquote' }
  };

  vetoableEventTypes = [
    'beforesourcemode',
    'beforeeditormode',
    'beforepaste'
  ];

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#contenteditable();
    this.#initToolbar();
    this.#initContent();
    this.modalElementsValue();
    this.#setParagraphSeparator();
    this.#attachEventHandlers();
    this.#initView();
    this.#updateTranslations();
    if (!this.height) this.height = defaultHeight;
    this.#syncAriaLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    // Cleanup marking that might still be present
    [
      ...qsAll('#errormessage-modal, #hyperlink-modal, #insertimage-modal', this.shadowRoot),
      ...qsAll(
        `ids-button,
        ids-separator,
        ids-menu-button,
        ids-popup-menu,
        ids-toolbar-section,
        ids-toolbar-more-actions,
        ids-toolbar`,
        (this as any)
      )
    ].flat().forEach((elem) => elem?.remove());
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [...super.attributes, ...EDITOR_ATTRIBUTES];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    this.reqInitialize = true;
    instanceCounter++;

    const ariaLabel = this.hasAttribute(attributes.LABEL_STATE) && this.label ? `${htmlAttributes.ARIA_LABEL}="${this.label}"` : '';
    const hiddenLabelCss = !this.label.length || this.labelState === 'hidden' ? ' empty' : '';
    const requiredLabelCss = !this.labelRequired ? ` ${CLASSES.labelRequired}` : '';

    const tmpl = parseTemplate(editorTemplate, {
      ariaLabel,
      disabled: this.disabled ? ' disabled' : '',
      readonly: this.readonly ? ' readonly' : '',
      contenteditable: this.disabled || this.readonly ? '' : ' contenteditable="true"',
      labelClass: `editor-label`,
      requiredLabelCss,
      hiddenLabelCss,
      placeholder: this.placeholder ? ` placeholder="${this.placeholder}"` : '',
      hiddenClass: CLASSES.hidden,
      labelText: this.label,
      sourceTextareaLabel: this.sourceTextareaLabel()
    });
    return tmpl;
  }

  /**
   * Sync the aria-label on the editable region with the label value.
   * When the label is hidden/collapsed the visible label is removed, so the
   * region relies on this aria-label (rather than aria-labelledby) for its name.
   * @private
   * @returns {void}
   */
  #syncAriaLabel(): void {
    const region = this.#elems?.editor ?? this.shadowRoot?.querySelector('.editor-container');
    if (!region) return;

    if (this.label && this.labelState) {
      // The visible label is removed, and its `aria-labelledby` target is now empty,
      // so name the region directly and drop the labelledby reference.
      region.setAttribute(htmlAttributes.ARIA_LABEL, this.label);
      region.removeAttribute(htmlAttributes.ARIA_LABELLED_BY);
    } else {
      region.removeAttribute(htmlAttributes.ARIA_LABEL);
      region.setAttribute(htmlAttributes.ARIA_LABELLED_BY, 'editor-label');
    }
  }

  /**
   * Set default value to each element in modals.
   * @param {object} [modals] Incoming modals options.
   * @param {object} [modals.hyperlink] The hyperlink options.
   * @param {string} [modals.hyperlink.url] Url for hyperlink.
   * @param {string} [modals.hyperlink.classes] Css Class for hyperlink.
   * @param {Array<object>} [modals.hyperlink.targets] List target options for hyperlink.
   * @param {boolean} [modals.hyperlink.isClickable] If true, isClickable checkbox should checked.
   * @param {boolean} [modals.hyperlink.showIsClickable] If true, will show isClickable checkbox.
   * @param {object} [modals.insertimage] The insertimage options.
   * @param {string} [modals.insertimage.url] Url for insertimage.
   * @param {string} [modals.insertimage.alt] Alt text for insertimage.
   * @returns {object} This API object for chaining
   */
  modalElementsValue(modals?: IdsEditorModals): object {
    const m = (isObject(modals) ? modals : {}) as IdsEditorModals;
    const d = EDITOR_DEFAULTS.modals;

    // Set hyperlink targets
    let hyperlinkTargets: Array<object> = [];
    if (m.hyperlink?.targets?.constructor === Array) {
      m.hyperlink.targets.forEach((target) => {
        if (isObject(target) && target.text) {
          const args: any = { text: target.text, value: target.value };
          if (target.selected) args.selected = true;
          hyperlinkTargets.push(args);
        }
      });
    } else {
      hyperlinkTargets = [...d.hyperlink.targets];
    }

    // Set defaults value for each modal
    this.#modals.defaults = {
      hyperlink: {
        url: m.hyperlink?.url ?? d.hyperlink.url,
        classes: m.hyperlink?.classes ?? d.hyperlink.classes,
        targets: hyperlinkTargets,
        targetSelected: hyperlinkTargets.find((t: any) => t.selected),
        isClickable: m.hyperlink?.isClickable ?? d.hyperlink.isClickable,
        showIsClickable: m.hyperlink?.showIsClickable ?? d.hyperlink.showIsClickable
      },
      insertimage: {
        url: m.insertimage?.url ?? d.insertimage.url,
        alt: m.insertimage?.alt ?? d.insertimage.alt
      }
    };

    this.#initModals();
    return this;
  }

  /**
   * Get label text for source textarea.
   * @returns {string} The label text for source textarea
   */
  sourceTextareaLabel(): string {
    return `${this.label} - HTML Source View`;
  }

  /**
   * Trigger the given event with current value.
   * @private
   * @param {string} eventtName The event name to be trigger.
   * @param {object|HTMLElement} target The target element.
   * @param {object} extra Extra data.
   * @returns {object} This API object for chaining.
   */
  #triggerEvent(eventtName: string, target: object | HTMLElement = this, extra: object = {}): object {
    this.triggerEvent(eventtName, target, {
      detail: { elem: this, value: this.value, ...extra }
    });
    return this;
  }

  /**
   * Init the current view
   * @private
   * @returns {object} This API object for chaining
   */
  #initView(): object {
    const shouldChange = this.#elems[this.view]?.classList?.contains(CLASSES.hidden);
    if (shouldChange) {
      if (/source/i.test(this.view)) this.#sourceMode();
      else this.#editorMode();
    }

    window.requestAnimationFrame(() => {
      this.#setSourceContent();
      this.resetDirtyTracker();
      this.#triggerEvent('initialize');
    });
    return this;
  }

  /**
   * Init the toolbar
   * @private
   * @returns {object} This API object for chaining
   */
  #initToolbar(): object {
    const slot = this.querySelector('[slot="toolbar"]');
    if (!slot) {
      this.insertAdjacentHTML('afterbegin', parseTemplate(toolbarTemplate, { instanceCounter }));
    }
    return this;
  }

  /** Handles the event when the editor's language changes */
  onLanguageChange = () => {
    this.#updateTranslations();
  };

  /** Handles setting the translations on the tooltips */
  #updateTranslations() {
    const toolbar = this.#elems.toolbar;
    if (!toolbar) return;
    const elements = toolbar.querySelectorAll('[translation-key]');
    elements.forEach((el: IdsButton) => {
      const key = String(el.getAttribute(attributes.TRANSLATION_KEY));
      el.tooltip = this.localeAPI?.translate(key);
      const audibleSpan = el.querySelector('span.audible');
      if (audibleSpan) audibleSpan.textContent = this.localeAPI?.translate(key);
    });
    toolbar.rebuildOverflowMenu();

    // Only translate modals if they exist
    if (this.#modals.hyperlink?.modal) {
      // Translate link modal
      this.#modals.hyperlink.modal.querySelector('[slot="title"]').textContent = this.localeAPI?.translate('InsertAnchor');
      this.#modals.hyperlink.elems?.url?.setAttribute(attributes.LABEL, this.localeAPI?.translate('Url'));
      this.#modals.hyperlink.elems?.classes?.setAttribute(attributes.LABEL, this.localeAPI?.translate('CssClass'));
      this.#modals.hyperlink.elems?.targets?.setAttribute(attributes.LABEL, this.localeAPI?.translate('Target'));
      this.#modals.hyperlink.elems?.clickable?.setAttribute(attributes.LABEL, this.localeAPI?.translate('Clickable'));
      if (this.#modals.hyperlink?.modal?.buttons) {
        this.#modals.hyperlink.modal.buttons[0].textContent = this.localeAPI?.translate('Cancel');
        this.#modals.hyperlink.modal.buttons[1].textContent = this.localeAPI?.translate('Apply');
      }
    }

    if (this.#modals.insertimage?.modal) {
      // Translate image modal
      this.#modals.insertimage.modal.querySelector('[slot="title"]').textContent = this.localeAPI?.translate('InsertHyperlink');
      this.#modals.insertimage.modal.querySelectorAll('ids-input')[0].setAttribute(attributes.LABEL, this.localeAPI?.translate('Url'));
      this.#modals.insertimage.modal.querySelectorAll('ids-input')[1].setAttribute(attributes.LABEL, this.localeAPI?.translate('AltText'));
      if (this.#modals.insertimage?.modal?.buttons) {
        this.#modals.insertimage.modal.buttons[0].textContent = this.localeAPI?.translate('Cancel');
        this.#modals.insertimage.modal.buttons[1].textContent = this.localeAPI?.translate('Apply');
      }
    }

    if (this.#modals.errormessage?.modal) {
      // Translate error message modal
      this.#modals.errormessage.modal.buttons[0].textContent = this.localeAPI?.translate('Ok');
      this.#modals.errormessage.modal.querySelector('[slot="title"]').textContent = this.localeAPI?.translate('SelectionRequired');
      this.#modals.errormessage.modal.querySelector('.demo-contents').textContent = this.localeAPI?.translate('HighlightText');
    }
  }

  /**
   * Get current selection
   * @private
   * @returns {Selection|null} The selection
   */
  #getSelection(): Selection | null {
    return document.getSelection();
  }

  /**
   * Set default paragraph separator
   * @private
   * @returns {object} This API object for chaining
   */
  #setParagraphSeparator(): object {
    document.execCommand('defaultParagraphSeparator', false, this.paragraphSeparator);
    this.#paragraphSeparator = this.paragraphSeparator !== 'br'
      ? this.paragraphSeparator : EDITOR_DEFAULTS.paragraphSeparator;
    return this;
  }

  /**
   * Set the heights and adjust the line number feature.
   * @private
   * @returns {void}
   */
  #adjustSourceLineNumbers(): void {
    window.requestAnimationFrame(() => {
      const lineHeight = parseInt(getComputedStyle(this.#elems.textarea).lineHeight, 10);
      const YPadding = 0;
      this.#elems.textarea.style.height = '';

      const scrollHeight = this.#elems.textarea.scrollHeight;
      const lineNumberCount = Math.floor((scrollHeight - YPadding) / lineHeight);
      const numberList = this.#elems.lineNumbers;
      const lastIdx = numberList.querySelectorAll('li').length;

      let list = '';
      let i = 0;
      let l;

      if (!this.#elems.lineNumberCount || lineNumberCount !== this.#elems.lineNumberCount) {
        if (!this.#elems.lineNumberCount) {
          // Build the list of line numbers from scratch
          this.#elems.lineNumberCount = lineNumberCount;
          for (l = this.#elems.lineNumberCount; i < l; i++) {
            list += `<li role="presentation"><span>${(i + 1)}</span></li>`;
          }
          numberList.insertAdjacentHTML('beforeend', list);
        } else if (this.#elems.lineNumberCount < lineNumberCount) {
          // Add extra line numbers to the bottom
          for (l = (lineNumberCount - this.#elems.lineNumberCount); i < l; i++) {
            list += `<li role="presentation"><span>${(lastIdx + i + 1)}</span></li>`;
          }
          numberList.insertAdjacentHTML('beforeend', list);
        } else if (this.#elems.lineNumberCount > lineNumberCount) {
          // Remove extra line numbers from the bottom
          i = this.#elems.lineNumberCount - lineNumberCount;
          [...numberList.querySelectorAll('li')].slice(-(i)).forEach((item) => item?.remove());
        }
        this.#elems.lineNumberCount = lineNumberCount;
      }

      if (this.#elems.textarea) this.#elems.textarea.style.height = `${numberList.scrollHeight}px`;
    });
  }

  /**
   * Copies content from textarea to editor
   */
  #setEditorContent() {
    const { textarea } = this.#elems;
    if (!textarea) return;
    let html = trimContent(textarea.value);
    html = cleanHtml(html);
    if (this.#editorTextContainer && this.#editorTextContainer.innerHTML !== html) {
      this.#editorTextContainer.innerHTML = html;
    }
  }

  /**
   * Copies editor content to textarea
   */
  #setSourceContent(): void {
    const { textarea } = this.#elems;
    if (!textarea) return;

    // if empty
    if (this.#editorTextContainer?.textContent?.replace('\n', '') === '') {
      this.#editorTextContainer.innerHTML = '';
    }

    // Create a temporary clone of the editor content to remove style tags
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = this.#editorTextContainer?.innerHTML ?? '';

    // Remove style tags
    tempContainer.querySelectorAll('style').forEach((style) => style.remove());

    // Remove empty class attributes
    tempContainer.querySelectorAll('[class=""]').forEach((elem) => {
      elem.removeAttribute('class');
    });

    let htmlContent = trimContent(tempContainer.innerHTML);
    htmlContent = this.sourceFormatter ? formatHtml(htmlContent) : htmlContent;
    if (textarea.value !== htmlContent) {
      textarea.value = htmlContent;
    }

    this.#applySelectionTextColor(textarea?.parentElement?.parentElement || null);
  }

  /**
   * Switch to editor mode
   * @returns {object|boolean} This API object for chaining, false if veto
   */
  #editorMode(): object | boolean {
    this.#elems.reqviewchange = true;

    // Fire the vetoable event.
    const args = { value: this.value, view: this.view };
    if (!this.triggerVetoableEvent('beforeeditormode', args)) {
      this.#triggerEvent('rejectviewchange');
      return false;
    }
    this.#setEditorContent();
    this.#toggleContentStyles(true);
    const elems = this.#elems;
    elems.btnSource.hidden = false;
    elems.btnEditor.hidden = true;
    elems.source.classList.add(CLASSES.hidden);
    elems.editor.classList.remove(CLASSES.hidden);
    elems.toolbar.disabled = false;
    elems.editor.focus();
    return this;
  }

  /**
   * Switch to source mode
   * @returns {object|boolean} This API object for chaining, false if veto
   */
  #sourceMode(): object | boolean {
    this.#elems.reqviewchange = true;

    // Fire the vetoable event.
    const args = { value: this.value, view: this.view };
    if (!this.triggerVetoableEvent('beforesourcemode', args)) {
      this.#triggerEvent('rejectviewchange');
      return false;
    }

    this.#toggleContentStyles(false);
    this.#setSourceContent();
    this.#adjustSourceLineNumbers();

    const elems = this.#elems;
    elems.btnSource.hidden = true;
    elems.btnEditor.hidden = false;
    elems.source.classList.remove(CLASSES.hidden);
    elems.editor.classList.add(CLASSES.hidden);
    elems.toolbar.disabled = true;
    elems.btnEditor.disabled = false;
    const moreActions = elems.toolbar?.querySelectorAll?.('ids-toolbar-more-actions');
    if (moreActions?.length > 0) {
      moreActions.forEach((actions: IdsToolbarMoreActions) => {
        actions.disabled = false;
      });
    }
    elems.textarea.focus();

    return this;
  }

  /**
   * Handle resize events
   */
  #resize(): void {
    if (this.view === 'source') this.#adjustSourceLineNumbers();
  }

  /**
   * Set contenteditable
   * @returns {object} This API object for chaining
   */
  #contenteditable(): object {
    const value = !this.disabled && !this.readonly;
    this.#editorTextContainer?.setAttribute(htmlAttributes.CONTENTEDITABLE, `${value}`);
    return this;
  }

  /**
   * Set disabled hyperlinks and keep tab order in sync
   * @returns {object} This API object for chaining
   */
  #disabledHyperlinks(): object {
    window.requestAnimationFrame(() => {
      if (this.disabled) {
        this.querySelectorAll('a').forEach((a: HTMLElement) => {
          const idx = a.getAttribute(attributes.TABINDEX);
          if (idx !== null) a.dataset.idsTabindex = idx;
          a.setAttribute(attributes.TABINDEX, '-1');
        });
      } else {
        this.querySelectorAll('a').forEach((a: HTMLElement) => {
          if (typeof a.dataset.idsTabindex === 'undefined') {
            a.removeAttribute(attributes.TABINDEX);
          } else {
            a.setAttribute(attributes.TABINDEX, a.dataset.idsTabindex);
            delete a.dataset.idsTabindex;
          }
        });
      }
    });
    return this;
  }

  /**
   * Initialize the raw content
   * @returns {object} This API object for chaining
   */
  #initContent(): object {
    if (!this.#editorTextContainer) {
      this.#editorTextContainer = document.createElement('div');
      this.#editorTextContainer.contentEditable = 'true';
      this.#editorTextContainer.slot = 'editor-slot';
      this.#editorTextContainer.classList.add('editor-container');
      this.#editorTextContainer.setAttribute(attributes.PLACEHOLDER, this.placeholder ?? '');
      this.append(this.#editorTextContainer);
      this.#applyEditorStyles();
    }

    // set colorpicker
    const setColorpicker = (key: string) => {
      const btn = this.querySelector<IdsButton>(`[editor-action="${key}"]`);
      if (btn) {
        let input = this.querySelector<HTMLElement>(`.${key}-input`);
        if (!input) {
          const elem = document.createElement('input');
          elem.setAttribute(attributes.TYPE, attributes.COLOR);
          elem.setAttribute(htmlAttributes.ARIA_LABEL, 'Text Color');
          elem.classList.add(`${key}-input`);
          elem.id = `${key}-input`;
          btn.append(elem);
          const elemLabel = document.createElement('label');
          elemLabel.setAttribute('for', `${key}-input`);
          elemLabel.id = `label-${key}-input`;
          input = this.querySelector(`.${key}-input`);
          btn.append(elemLabel);
        }
        const cssText = 'border:0;padding:0;margin:0;height:100%;width:100%;opacity:0;position:absolute;top:0;left:0;';
        if (input) input.style.cssText = cssText;
        this.#elems[`${key}Input`] = input;
        const btnIcon = btn.querySelector<IdsIcon>('ids-icon');
        const colorPickerSVGDefs = `<defs>
          <linearGradient id="colorpicker-underline" gradientTransform="rotate(90)">
            <stop class="stop-base" offset="80%"></stop>
            <stop class="stop-underline" offset="20%"></stop>
          </linearGradient>
        </defs>`;
        btnIcon?.classList.add('editor-forecolor');
        btnIcon?.pathElem?.setAttribute('fill', 'url(#colorpicker-underline)');
        btnIcon?.appendSVGDefs(colorPickerSVGDefs);
      }
    };
    setColorpicker('forecolor');
    setColorpicker('backcolor');

    // Set source/editor mode buttons
    let btnSource = this.querySelector<IdsButton>('[editor-action="sourcemode"]');
    let btnEditor = this.querySelector<IdsButton>('[editor-action="editormode"]');
    if (btnSource || btnEditor) {
      btnSource?.container?.setAttribute(attributes.EDITOR_ACTION, 'sourcemode');
      btnEditor?.container?.setAttribute(attributes.EDITOR_ACTION, 'editormode');
      if (!btnEditor) {
        const template = document.createElement('template');
        template.innerHTML = btnEditorModeTemplate;
        btnSource?.after(template.content.cloneNode(true));
        btnEditor = this.querySelector('[editor-action="editormode"]');
      }
      if (!btnSource) {
        const template = document.createElement('template');
        template.innerHTML = btnSourceModeTemplate;
        btnEditor?.after(template.content.cloneNode(true));
        btnSource = this.querySelector('[editor-action="sourcemode"]');
      }
      if (this.view === 'source') {
        btnSource?.setAttribute(attributes.HIDDEN, 'true');
        btnEditor?.setAttribute(attributes.HIDDEN, 'false');
      } else {
        btnSource?.setAttribute(attributes.HIDDEN, 'false');
        btnEditor?.setAttribute(attributes.HIDDEN, 'true');
      }
    }

    // Set to cache some elements
    this.#elems.main = qs('.main-container', this.shadowRoot);
    this.#elems.editor = qs('.editor-container', this.shadowRoot);
    this.#elems.editorSlot = qs('slot#editor-slot', this.shadowRoot);
    this.#elems.source = qs('.source-container', this.shadowRoot);
    this.#elems.lineNumbers = qs('.line-numbers', this.shadowRoot);
    this.#elems.textarea = qs('#source-textarea', this.shadowRoot);
    this.#elems.toolbar = this.querySelector('ids-toolbar');
    this.#elems.btnSource = this.querySelector('[editor-action="sourcemode"]');
    this.#elems.btnEditor = this.querySelector('[editor-action="editormode"]');

    this.#elems.toolbarElms = this.querySelectorAll('[editor-action]');
    this.#elems.forecolorBtn = this.querySelector('[editor-action="forecolor"]');
    this.#elems.backcolorBtn = this.querySelector('[editor-action="backcolor"]');
    this.#elems.blockquoteBtn = this.querySelector('[editor-action="blockquote"]');
    this.#elems.hyperlinkBtn = this.querySelector('[editor-action="hyperlink"]');

    // Formatblock
    this.#elems.formatblock = { btn: this.querySelector('[editor-action="formatblock"]'), items: {} };
    this.#elems.formatblock.btn?.configureMenu();
    this.#elems.formatblock.btn?.menuEl?.items?.forEach((item: any) => {
      const text = item.text || item.textContent?.trim();
      const translationKeyElem = item.querySelector(`[${attributes.TRANSLATION_KEY}]`);
      const translationKey = translationKeyElem?.getAttribute(attributes.TRANSLATION_KEY);
      const translationParams = translationKeyElem?.getAttribute(attributes.TRANSLATION_PARAMS);
      this.#elems.formatblock.items[item.value] = {
        text,
        value: item.value,
        translationKey,
        translationParams
      };
    });

    // Use for dirty-tracker and validation
    this.input = this.#elems.textarea;
    this.labelEl = qs('#editor-label', this.shadowRoot);
    if (this.validate) {
      this.validationEvents = 'change.editorvalidation input.editorvalidation blur.editorvalidation';
      this.validationElems = {
        main: this.#elems.main,
        editor: this.#elems.editor
      };
    }
    return this;
  }

  /**
   * Initialize the modals to attach with editor
   * @private
   * @returns {object} This API object for chaining
   */
  #initModals(): object {
    const appendModal = (key: string, btn: HTMLElement | null, html: string) => {
      const template = document.createElement('template');
      template.innerHTML = html;
      this.container?.appendChild(template.content.cloneNode(true));
      this.#modals[key] = { btn, modal: qs(`#${key}-modal`, this.shadowRoot) };
    };
    const hyperlinkBtn = this.querySelector<IdsButton>('[editor-action="hyperlink"]');
    const insertimageBtn = this.querySelector<IdsButton>('[editor-action="insertimage"]');

    // Remove elements
    const removeElems = [
      qs('#errormessage-modal', this.shadowRoot),
      qs('#hyperlink-modal', this.shadowRoot),
      qs('#insertimage-modal', this.shadowRoot),
    ];
    removeElems.forEach((elem) => elem?.remove());

    // Error message
    appendModal('errormessage', null, errorMessageTemplate);
    this.#modals.errormessage.btn = qs('#errormessage-modal-ok', this.shadowRoot);

    // Hyperlink
    if (hyperlinkBtn) {
      const key = 'hyperlink';
      const btn = hyperlinkBtn;
      const {
        url,
        classes,
        targets,
        targetSelected,
        isClickable,
        showIsClickable
      } = this.#modals.defaults.hyperlink;

      const clickableElemHtml = !showIsClickable ? ''
        : `<ids-checkbox id="${key}-modal-checkbox-clickable" label="Clickable in editor"${isClickable ? ' checked="true"' : ''}></ids-checkbox>`;

      let targetDropdownHtml = '';
      if (targets.length) {
        const options = targets.map((t: { value: string, text: string }) => `<ids-list-box-option value="${t.value}">${t.text}</ids-list-box-option>`).join('');
        const val = targetSelected?.value ?? '';
        targetDropdownHtml = `
          <ids-dropdown id="${key}-modal-dropdown-targets" label="Target" value="${val}">
            <ids-list-box>${options}</ids-list-box>
          </ids-dropdown>`;
      }
      const html = parseTemplate(hyperlinkModalTemplate, {
        key,
        url,
        clickableElemHtml,
        classes,
        targetDropdownHtml,
        hiddenClass: CLASSES.hidden
      });
      appendModal(key, btn, html);
      this.#modals[key].elems = {
        url: qs(`#${key}-modal-input-url`, this.shadowRoot),
        clickable: qs(`#${key}-modal-checkbox-clickable`, this.shadowRoot),
        classes: qs(`#${key}-modal-input-classes`, this.shadowRoot),
        targets: qs(`#${key}-modal-dropdown-targets`, this.shadowRoot),
        removeContainer: qs(`#${key}-modal-checkbox-remove-container`, this.shadowRoot),
        removeElem: qs(`#${key}-modal-checkbox-remove`, this.shadowRoot)
      };
    }

    // Insert Image
    if (insertimageBtn) {
      const key = 'insertimage';
      const btn = insertimageBtn;
      const { url, alt } = this.#modals.defaults.insertimage;
      const html = parseTemplate(insertimageModalTemplate, { key, url, alt });
      appendModal(key, btn, html);
    }

    // Attach events to each modal
    ['errormessage', 'insertimage', 'hyperlink'].forEach((key) => {
      if (this.#modals[key]?.modal) {
        this.#attachModalEvents(key);
      }
    });

    return this;
  }

  /**
   * Set toolbar buttons as un-active.
   * @private
   * @returns {void}
   */
  #unActiveToolbarButtons(): void {
    this.#elems.toolbarElms?.forEach((btn: any) => {
      if (btn) btn.cssClass = [];

      // remove underline style for color picker
      if (btn.getAttribute(attributes.EDITOR_ACTION) === 'forecolor') {
        btn.querySelector('ids-icon')?.classList.remove('is-active');
      }
    });
  }

  /**
   * On paste editor container.
   * @private
   * @param {ClipboardEvent} e The event
   * @returns {void}
   */
  #onPasteEditorContainer(e: ClipboardEvent): void {
    if (!e || this.view !== 'editor') return;

    // Only prevent default if pasting into the editor content, not modal inputs
    const selection = window.getSelection();
    if (!selection || !this.#contentContainsFocusNode(selection.focusNode)) {
      return;
    }

    e.preventDefault();
    const asPlainText = handlePasteAsPlainText(e);
    const asHtml = handlePasteAsHtml(e);
    const args = {
      asHtml,
      asPlainText,
      value: this.value,
      view: this.view
    };
    if (!this.triggerVetoableEvent('beforepaste', args)) {
      this.#triggerEvent('rejectpaste', this, args);
      return;
    }

    if (document.queryCommandSupported('insertText') && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      // Remove inline CSS from content
      const stripInlineCSS = (html: string): string => html.replace(/\sstyle\s*=\s*["'][^"']*["']/gi, '');

      const cleanContent = this.pasteAsPlainText
        ? (asPlainText ?? '')
        : stripInlineCSS(asHtml ?? '');

      const fragment = range.createContextualFragment(cleanContent);
      range.insertNode(fragment);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
      (debounce(() => this.#triggerEvent('afterpaste'), 410) as any)();
    }
  }

  /**
   * Check if slotted content contains current selection focus node
   * @param {Node} focusNode Selection focus node
   * @returns {boolean} true if focus node is within editor
   */
  #contentContainsFocusNode(focusNode: Node | null): boolean {
    return this.#editorTextContainer?.contains(focusNode) || false;
  }

  /**
   * On selection change.
   * @param {Selection} selection Selection Object
   * @private
   * @returns {void}
   */
  #onSelectionChange(selection: Selection): void {
    const elems = this.#elems;
    const parents: any = selectionParents(selection, this.#editorTextContainer!);
    const setActive = (btn: any) => {
      if (btn) btn.cssClass = ['is-active'];
    };
    const regxFormatblock = new RegExp(`^(${Object.keys(elems.formatblock.items).join('|')})$`, 'i');
    Object.entries(this.#actions).forEach(([k, v]) => {
      if (k.substring(0, 5) === 'align') return;
      if (k === 'forecolor' && parents.font?.node?.hasAttribute('color')) {
        setActive(elems.forecolorBtn);
        this.#setForecolorActiveColor(elems.forecolorBtn, parents.font?.node?.getAttribute('color'));
      }
      if (k === 'backcolor' && parents.span?.node?.style?.backgroundColor) {
        setActive(elems.backcolorBtn);
      }
      if (k === 'blockquote' && !!parents.blockquote) {
        setActive(elems.blockquoteBtn);
      }
      if (k === 'hyperlink' && !!parents.a) {
        setActive(elems.hyperlinkBtn);
      }
      if (elems.formatblock?.btn && regxFormatblock.test(k) && !!parents[k]) {
        let text = this.localeAPI?.translate(elems.formatblock.items[k].translationKey) || '';
        const param = elems.formatblock.items[k].translationParams;
        if (param) text = text.replace(`{0}`, param);

        elems.formatblock.btn.text = text;
      }
      if (document.queryCommandState((<any>v).action) && this.#contentContainsFocusNode(selection.focusNode)) {
        setActive(this.querySelector(`[editor-action="${k}"]`));
      }
    });
  }

  /**
   * Updates forecolor toolbar button with selected font color
   * @param {IdsButton} btn forecolor button
   * @param {string} color hex color
   */
  #setForecolorActiveColor(btn: IdsButton | null, color: string): void {
    if (!btn || !color) return;
    const btnIcon = btn.querySelector<IdsIcon>('ids-icon');
    btnIcon?.classList.add('is-active');
    btnIcon?.style.setProperty('--forecolor-active-color', color);
  }

  /**
   * On toolbar items selected.
   * @param {CustomEvent} e The event
   */
  #onSelectedToolbar(e: CustomEvent): void {
    if (!e?.detail?.elem) return;

    const elem = e.detail.elem;
    let target = null;

    if (/^ids-menu-item$/i.test(elem.nodeName)) target = elem.menu?.target;
    if (/^ids-button$/i.test(elem.nodeName)) target = elem;

    const action = target?.getAttribute(attributes.EDITOR_ACTION);

    if (this.#actions[action]) {
      let value = null;
      if (action === 'formatblock') {
        const menuBtn = elem.menu?.target;
        const sel = document.getSelection();
        if (menuBtn) menuBtn.text = elem.text || elem.textContent?.trim();
        value = e.detail.value;
        // format dropdown changes selection
        // previous editor selection must be restored
        if (sel) {
          restoreObjectSelection(this.#savedSelection);
        }
      }

      if (/^(hyperlink|insertimage)$/i.test(action)) {
        this.#modals[action]?.modal?.show();
      } else {
        this.#handleAction(action, value);
      }
      this.#triggerEvent('input', this.#elems.editor);
      this.#toggleContentStyles(true);
    }
  }

  /**
   * On toolbar items input event.
   * @param {MouseEvent} e The event
   */
  #onInputToolbar(e: MouseEvent): void {
    if (!e) return;

    if (/forecolor-input|backcolor-input/i.test((<any>e.target).className)) {
      const action = /forecolor-input/i.test((<any>e.target).className) ? 'forecolor' : 'backcolor';
      const a = { ...this.#actions[action], value: (<any>e.target).value };

      if (this.#savedSelection && window.getSelection()?.toString() === '') {
        restoreObjectSelection(this.#savedSelection);
      }
      document.execCommand(a.action, false, a.value);
    }
  }

  /**
   * On modal before show.
   * @param {string} key The modal key
   * @returns {boolean} false if, should not proseed
   */
  #onBeforeShowModal(key: string): boolean {
    const sel: any = this.#getSelection();
    if (!this.#savedSelection) {
      this.#savedSelection = saveSelectionObject(sel);
    }
    if (!this.#savedSelection) return false;

    // Rest all values;
    this.#modals.beforeShowValues = {};

    if (key === 'hyperlink') {
      const args = { ...this.#modals.defaults.hyperlink, hideRemoveContainer: true };
      const elems = { ...this.#modals.hyperlink.elems };
      const currentLink = findElementInSelection(sel, this, 'a');

      if (currentLink) {
        args.currentLink = currentLink;
        args.hideRemoveContainer = false;
        args.url = currentLink?.getAttribute(attributes.HREF) ?? '';
        args.classes = currentLink?.getAttribute(attributes.CSS_CLASS) ?? '';
        args.isClickable = currentLink?.getAttribute('contenteditable') === 'false';
        args.targetSelected = { value: currentLink?.getAttribute(attributes.TARGET) ?? '' };
      }
      const opt = args.hideRemoveContainer ? 'add' : 'remove';
      elems.removeContainer?.classList[opt](CLASSES.hidden);
      if (elems.url) {
        elems.url.value = args.url;
        elems.url.checkValidation();
      }
      if (elems.classes) elems.classes.value = args.classes;
      if (elems.targets) elems.targets.value = args.targetSelected?.value ?? '';
      if (elems.clickable) elems.clickable.checked = args.isClickable;
      this.#modals.beforeShowValues.hyperlink = { ...args };
    }
    return true;
  }

  /**
   * Formats selected text to h1, h2, h3, or normal
   * @param {Selection} selection Selection object
   * @param {EditorAction} action action config
   */
  #formatBlock(selection: Selection, action: EditorAction): void {
    if (!action) return;

    // Store the current block and its content before formatting
    const currentBlock = blockElem(selection);

    // Add null check for currentBlock.el before accessing textContent
    if (!currentBlock || !currentBlock.el) return;

    const currentContent = currentBlock.el.textContent || '';
    const isBlockquote = action.value === 'blockquote';

    if (isBlockquote && blockElem(selection).tagName === 'blockquote') {
      action = { ...this.#actions[this.#paragraphSeparator as any] };
    }

    const selectionBlock = selectionBlockElems(selection, this.#editorTextContainer!);
    if (selectionBlock.length === 1) {
      document.execCommand('formatBlock', false, action.value);
    } else {
      selectionBlock.forEach((elem) => {
        const regx = new RegExp(`<(/?)${elem.tagName}((?:[^>"']|"[^"]*"|'[^']*')*)>`, 'gi');
        const html = elem.outerHTML.replace(regx, `<$1${action.value}$2>`);
        elem.outerHTML = html;
      });
    }

    this.#toggleBlockFormatStyles(true);

    // Use requestAnimationFrame to ensure DOM is updated
    window.requestAnimationFrame(() => {
      const container = this.#editorTextContainer;
      if (container) {
        // Find the formatted element that matches our original content
        const formattedElement = Array.from(container.children).find(
          (child) => child.textContent === currentContent
        );

        if (formattedElement && !isBlockquote) {
          const range = document.createRange();
          const sel = window.getSelection();

          // Set cursor to the end of the matched element
          range.selectNodeContents(formattedElement);
          range.collapse(false); // false means collapse to end

          if (sel) {
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      }
    });
  }

  /**
   * Sets text alignment to selection
   * @param {Selection} selection Selection object
   * @param {EditorAction} action action config
   */
  #alignText(selection: Selection, action: EditorAction): void {
    const alignDoc = this.localeAPI?.isRTL() ? 'right' : 'left';
    const align = action.action.replace('align', '').toLowerCase();
    selectionBlockElems(selection, this.#editorTextContainer!).forEach((elem) => {
      if (align === alignDoc) {
        elem?.style.removeProperty('text-align');
      } else {
        elem?.style.setProperty('text-align', align);
      }
    });
  }

  /**
   * Sets text color to selection
   * @param {Selection} selection Selection object
   * @param {EditorAction} action action config
   */
  #setTextColor(selection: Selection, action: EditorAction): void {
    if (this.#savedSelection && this.#elems[`${action.action}Input`]) {
      const color = action.value === 'backcolor'
        ? (selection?.focusNode?.parentNode as HTMLElement)?.style.getPropertyValue?.('background-color')
        : document.queryCommandValue(action.action);
      this.#elems[`${action.action}Input`].value = /rgb/i.test(color) ? rgbToHex(color) : color;
    }
  }

  /**
   * Formats selection into ordererd/unordered list
   * @param {Selection} selection Selection object
   * @param {EditorAction} action action config
   */
  #createList(selection: Selection, action: EditorAction): void {
    let isAdd = true;
    const listTagType = action.action === 'insertOrderedList' ? '<ol>' : '<ul>';

    // Only get elements that are actually part of the list or being converted to a list
    const elementsToModify = selectionBlockElems(selection, this.#editorTextContainer!)
      .filter((elem) => elem.tagName === 'LI'
              || elem.parentElement?.tagName === 'UL'
              || elem.parentElement?.tagName === 'OL'
              || selection.containsNode(elem, false));

    elementsToModify.forEach((elem) => {
      if (elem.innerHTML.includes(listTagType)) isAdd = false;

      // Only modify the HTML of elements that are actually part of the list
      if (elem.tagName === 'LI' || elem.parentElement?.tagName === 'UL' || elem.parentElement?.tagName === 'OL') {
        elem.innerHTML = (elem.innerHTML as any)
          .replaceAll('</ul>', '')
          .replaceAll('</ol>', '')
          .replaceAll('</li>', '')
          .replaceAll('<ul>', '')
          .replaceAll('<ol>', '')
          .replaceAll('<li>', '');
      }
    });

    if (isAdd) {
      document.execCommand(action.action, false, action.value);
    }
  }

  /**
   * Create hyperlink at selection
   * @param {Range} range Selection range
   */
  #createHyperlink(range: Range): void {
    const elems = { ...this.#modals.hyperlink.elems };
    const { currentLink, hideRemoveContainer } = this.#modals.beforeShowValues.hyperlink;
    const urlValue = elems?.url?.value ?? '';
    const classValue = elems?.classes?.value;
    const targetValue = elems.targets?.value;
    const clickableValue = elems.clickable?.checked ? 'false' : '';
    const attr = (link: HTMLElement, name: string, value: string) => {
      if (value) link.setAttribute(name, value);
      else link.removeAttribute(name);
    };

    if (hideRemoveContainer) {
      // Create new hyperlink
      if (urlValue) {
        const rangeString = range.toString();
        const aLink = document.createElement('a');
        attr(aLink, attributes.HREF, urlValue);
        attr(aLink, attributes.CSS_CLASS, classValue);
        attr(aLink, attributes.TARGET, targetValue);
        attr(aLink, 'contenteditable', clickableValue);

        if (rangeString) {
          range.surroundContents(aLink);
        } else {
          aLink.textContent = urlValue;
          range.insertNode(aLink);
        }
      }
    } else if (elems.removeElem?.checked || urlValue === '') {
      // Remove the current hyperlink, selection was on hyperlink
      currentLink.outerHTML = currentLink.innerHTML;
    } else {
      // Update the current hyperlink, selection was on hyperlink
      attr(currentLink, attributes.HREF, urlValue);
      attr(currentLink, attributes.CSS_CLASS, classValue);
      attr(currentLink, attributes.TARGET, targetValue);
      attr(currentLink, 'contenteditable', clickableValue);
    }

    // Reset all hyperlink related elements in modal, for next time open
    elems.removeElem.checked = false;
    Object.entries(elems)
      .filter(([k]) => (!(/^(removeElem|removeContainer)$/.test(k))))
      .map((x) => x[1])
      .forEach((elem: any) => {
        if (elem) elem.disabled = false;
      });

    this.#toggleHyperlinkStyles(true);
  }

  /**
   * Create image at selection
   */
  #createImage(): void {
    const rawUrl = qs(`#insertimage-modal-input-src`, this.shadowRoot)?.value ?? '';

    // Clean URL to remove extra text like dimensions
    let url = rawUrl.trim().replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+.*$/, '');

    // If URL is not absolute, prepend base URL
    if (!/^https?:\/\/|^data:/i.test(url)) {
      const baseUrl = this.getAttribute('base-url') || window.location.origin;
      url = `${baseUrl}/${url}`;
    }

    const alt = qs(`#insertimage-modal-input-alt`, this.shadowRoot).value ?? '';
    if (!alt) {
      // Require alt text for accessibility
      const altInput = qs(`#insertimage-modal-input-alt`, this.shadowRoot);
      altInput.validate = 'required';
      altInput.checkValidation();
      return;
    }

    // Create image as block element
    const imgHtml = `<p><img src="${url}" alt="${alt}" /></p>`;
    document.execCommand('insertHTML', false, imgHtml);
  }

  /**
   * Handle given action
   * @param {string} actionName Name of action
   * @param {string} val The option value
   * @returns {void}
   */
  #handleAction(actionName: string, val = ''): void {
    const a: EditorAction = { ...this.#actions[actionName] };
    const sel = window.getSelection();

    // Switch editor/source mode
    if (/^(editormode|sourcemode)$/i.test(actionName)) {
      this.handleChangeEditorMode(actionName);
      return;
    }

    if (!sel) return;

    restoreObjectSelection(this.#savedSelection);
    // Set text format
    if (a.action === 'formatBlock') {
      this.#savedSelection = saveSelectionObject(sel);
      const result = this.#formatBlock(sel, this.#actions[val] ?? this.#actions[actionName]);
      restoreObjectSelection(this.#savedSelection);
      return result;
    }

    // Set text align
    if (/^(alignleft|alignright|aligncenter|alignjustify)$/i.test(actionName)) {
      return this.#alignText(sel, this.#actions[actionName]);
    }

    // Set forecolor, backcolor
    if (/^(forecolor|backcolor)$/i.test(actionName)) {
      if (/^(forecolor)$/i.test(actionName)) {
        const isOverflowed = this.#elems.toolbar.querySelector('ids-button[editor-action="forecolor"]').hasAttribute('overflowed');
        const input = this.querySelector<HTMLElement>(`#label-${actionName}-input`);
        const dropdown = this.querySelector<HTMLElement>(`#${actionName}-input`);

        if (isOverflowed) {
          dropdown!.style.left = '-220px';
        } else {
          dropdown!.style.left = 'unset';
        }
        input?.click();
      }
      return this.#setTextColor(sel, this.#actions[actionName]);
    }

    // Set ordered list, unordered list
    if (/^(orderedlist|unorderedlist)$/i.test(actionName)) {
      return this.#createList(sel, this.#actions[actionName]);
    }

    // Exec command all other actions
    // NOTE: execCommand() is deprecated
    a.value = a.value ?? val;
    restoreObjectSelection(this.#savedSelection);
    document.execCommand(a.action, false, a.value);
  }

  /**
   * Handle modal action.
   * @private
   * @param {string} key The modal key
   * @returns {void}
   */
  #handleModalAction(key: string): void {
    const sel = this.#getSelection();
    if (!sel || !this.#actions[key]) return;

    restoreObjectSelection(this.#savedSelection);
    const range = sel?.getRangeAt(0);

    // Insert image
    if (key === 'insertimage') {
      this.#createImage();
    }

    if (key === 'hyperlink') {
      this.#createHyperlink(range);
    }
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} The object for chaining.
   */
  #attachEventHandlers(): object {
    // Attach selection change
    this.onEvent('selectionchange.editor', document, debounce(() => {
      const selection = document.getSelection();
      const isSelectedInEditor = this.#contentContainsFocusNode(selection?.focusNode ?? null);
      this.#unActiveToolbarButtons();
      this.#elems.main.classList.toggle('focused', isSelectedInEditor);

      if (selection?.focusNode && isSelectedInEditor) {
        this.#savedSelection = saveSelectionObject(selection);
        this.#onSelectionChange(selection);
      }
    }, 200));

    // Attach toolbar events
    this.onEvent('selected.editor-toolbar', this.#elems.toolbar, (e: CustomEvent) => {
      this.#onSelectedToolbar(e);
    });
    this.onEvent('input.editor-toolbar', this.#elems.toolbar, (e: MouseEvent) => {
      this.#onInputToolbar(e);
    });
    this.onEvent('focusin.editor-toolbar', this.#elems.toolbar, () => {
      this.#unActiveToolbarButtons();
    });

    // Textarea
    this.onEvent('input.editor-textarea', this.#elems.textarea, (evt) => {
      evt.stopPropagation();
      this.#adjustSourceLineNumbers();
      (debounce(() => {
        if (!this.#elems.reqviewchange) this.#triggerEvent('change', this.#elems.textarea);
      }, 400) as any)();
    });
    this.onEvent('change.editor-textarea', this.#elems.textarea, () => {
      this.#triggerEvent('change');
    });

    // Editor container
    this.onEvent('input.editor-editcontainer', this, debounce(() => {
      if (!this.#elems.reqviewchange) {
        this.#setSourceContent();
        this.#triggerEvent('change', this.#elems.textarea);
      }
    }, 400));
    this.onEvent('blur.editor-editcontainer', this, () => {
      this.#triggerEvent('blur', this.#elems.textarea);
    });
    this.onEvent('paste.editor-editcontainer', this, (e: ClipboardEvent) => {
      this.#onPasteEditorContainer(e);
    });

    // Other events
    this.#attachKeyboardEvents();

    // Set observer for resize
    this.#resizeObserver.disconnect();
    if (this.container) this.#resizeObserver.observe(this.container);

    // EPC: @TODO replace this with the setting from infor-design/enterprise-wc#488
    const moreActions = this.querySelector<IdsToolbarMoreActions>('ids-toolbar-more-actions');
    this.onEvent('beforeshow.more-actions', moreActions, () => {
      const popupContainer = moreActions?.menu?.popup?.container;
      const currentWidth = popupContainer?.style.width;
      if (!currentWidth && popupContainer) {
        popupContainer.style.width = '175px';
      }
      this.#savedSelection = saveSelectionObject(window.getSelection()!);
    });

    // Remove flags
    this.onEvent('viewchange.editor-reqviewchange', this, debounce(() => {
      delete this.#elems.reqviewchange;
    }, 410));
    this.onEvent('rejectviewchange.editor-reqviewchange', this, debounce(() => {
      delete this.#elems.reqviewchange;
    }, 410));
    this.onEvent('initialize.editor-initialize', this, debounce(() => {
      delete this.reqInitialize;
    }, 410));

    // Editor Slot change
    this.offEvent('slotchange.hidden-slot', this.hiddenSlot);
    this.onEvent('slotchange.hidden-slot', this.hiddenSlot, () => {
      // move user provided elements to internally handled text container slot dev#editor-slot
      this.#editorTextContainer?.append(...this.hiddenSlot.assignedElements());
    });

    this.offEvent('slotchange.editor-slot', this.editorSlot);
    this.onEvent('slotchange.editor-slot', this.editorSlot, () => {
      this.#toggleContentStyles(true);
    });

    // GenAI button functionality
    const genAIBusyIndicator = document.createElement('ids-loading-indicator') as IdsLoadingIndicator;
    genAIBusyIndicator.setAttribute('generative-ai', '');
    let isAIBusy = false;

    this.offEvent('click.editor-genai', genAIBusyIndicator);
    this.onEvent('selected.editor-genai', this, (e: CustomEvent) => {
      const target = e.detail?.elem;
      if (target?.getAttribute?.(attributes.EDITOR_ACTION) === 'generativeai') {
        if (isAIBusy) {
          genAIBusyIndicator?.remove();
        } else {
          this.append(genAIBusyIndicator);
        }
        target.toggleAnimation?.(!isAIBusy);
        isAIBusy = !isAIBusy;
      }
    });

    return this;
  }

  #toggleBlockFormatStyles(applyStyles: boolean): void {
    const blockQuoteElems = this.#editorTextContainer!.querySelectorAll<HTMLElement>('blockquote');
    const firstParagraph = this.#editorTextContainer!.querySelector<HTMLParagraphElement>('p:first-of-type');

    if (!applyStyles) {
      firstParagraph?.classList.remove('editor-first-paragraph');
      blockQuoteElems.forEach((elem) => elem.classList.remove('editor-blockquote'));
      return;
    }

    blockQuoteElems.forEach((block) => {
      block.classList.add('editor-blockquote');
    });

    // Add styling to first paragraph
    firstParagraph?.classList.add('editor-first-paragraph');
  }

  /** Remove margin from first paragraph */
  #adjustFirstParagraph() {
    const firstParagraph = this.#editorTextContainer!.querySelector<HTMLParagraphElement>('p:first-of-type');
    if (firstParagraph) {
      firstParagraph.classList.add('editor-first-paragraph');
    }
  }

  #applySelectionTextColor(element: HTMLElement | null) {
    if (!element) {
      return;
    }

    const style = document.createElement('style');
    style.innerHTML = `::selection { color:var(--ids-editor-color-text-selected); background-color:var(--ids-editor-color-background-selected) }`;
    style.setAttribute('nonce', '0a59a005');
    element?.append(style);
  }

  /**
   * Apply styles to the editor content - public method for external use
   * @returns {object} This API object for chaining
   */
  applyEditorStyles(): object {
    this.#applyEditorStyles();
    return this;
  }

  /**
   * Apply styles to the editor content using a nonce-based style element
   */
  #applyEditorStyles(): void {
    // Remove any existing style element
    const existingStyle = this.#editorTextContainer?.querySelector('style[data-editor-styles]');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create a new style element with the necessary styles
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-editor-styles', 'true');
    styleEl.setAttribute('nonce', '0a59a005');
    styleEl.textContent = `
      blockquote {
        border-inline-start: var(--ids-editor-blockquote-border-inline-start);
        margin-inline-start: var(--ids-editor-blockquote-margin-inline-start);
        margin-inline-end: var(--ids-editor-blockquote-margin-inline-end);
        padding-inline-start: var(--ids-editor-blockquote-padding-inline-start);
      }
      p:first-of-type {
        margin-block-start: 0;
      }
      a, .editor-link {
      color: var(--ids-editor-link-color-text-link);
      }
      .editor-text-align-left { text-align: left; }
      .editor-text-align-center { text-align: center; }
      .editor-text-align-right { text-align: right; }
      .editor-text-align-justify { text-align: justify; }
    `;

    // Append the style element to the editor container
    this.#editorTextContainer?.appendChild(styleEl);
  }

  #toggleHyperlinkStyles(applyStyles: boolean) {
    const linkElems = this.#editorTextContainer!.querySelectorAll<HTMLAnchorElement>('a');

    linkElems.forEach((link) => {
      if (applyStyles) {
        link.classList.add('editor-link');
      } else {
        link.classList.remove('editor-link');
      }
    });
  }

  #toggleContentStyles(applyStyles: boolean) {
    if (applyStyles) {
      this.#applyEditorStyles();
    }
    this.#toggleHyperlinkStyles(applyStyles);
    this.#toggleBlockFormatStyles(applyStyles);
  }

  /**
   * Attach modal events
   * @private
   * @param {string} key The modal key
   * @returns {void}
   */
  #attachModalEvents(key: string): void {
    const modal = this.#modals[key].modal;

    // Hide modal
    modal.onButtonClick = () => {
      modal?.hide();
    };

    // No need to bind else, if modal has no target-btn
    if (!this.#modals[key].btn) return;

    // Before modal open
    this.offEvent(`beforeshow.editor-modal-${key}`, modal);
    this.onEvent(`beforeshow.editor-modal-${key}`, modal, (e: CustomEvent) => {
      if (!this.#onBeforeShowModal(key) && key !== 'errormessage') {
        e.detail.response(false);
        this.#modals.errormessage?.modal?.show();
      }
    });

    // Apply button clicked
    this.offEvent(`click.editor-modal-${key}`, modal);
    this.onEvent(`click.editor-modal-${key}`, modal, (e: MouseEvent) => {
      if ((e.target as any).getAttribute(attributes.ID) === `${key}-modal-apply-btn`) {
        // Check all validatable elements in the modal
        const inputs = modal.querySelectorAll('[validate]');
        let isValid = true;

        inputs.forEach((input: any) => {
          if (input.validate) {
            input.checkValidation();
            if (input.validationMessagesCount > 0) {
              isValid = false;
            }
          }
        });

        if (!isValid) {
          return; // Don't close modal if any validation fails
        }

        if (key === 'insertimage') {
          // Try to restore selection and insert at caret, but if not possible, insert at start of editor
          this.#editorTextContainer?.focus();
          setTimeout(() => {
            let sel = window.getSelection();
            let restored = false;
            if (this.#savedSelection) {
              try {
                restoreObjectSelection(this.#savedSelection);
                sel = window.getSelection();
                if (sel && sel.rangeCount > 0 && this.#contentContainsFocusNode(sel.focusNode)) {
                  this.#handleModalAction(key);
                  restored = true;
                }
              } catch (err) {
                // ignore
              }
            }
            if (!restored) {
              // Fallback: insert image at start of editor
              const src = qs(`#insertimage-modal-input-src`, this.shadowRoot)?.value ?? '';
              const alt = qs(`#insertimage-modal-input-alt`, this.shadowRoot)?.value ?? '';
              if (src && this.#editorTextContainer) {
                const img = document.createElement('img');
                img.src = src;
                img.alt = alt;
                this.#editorTextContainer.insertBefore(img, this.#editorTextContainer.firstChild);
              }
            }
            modal?.hide();
          }, 0);
        } else {
          this.#handleModalAction(key);
        }
      }
      if ((e.target as any)?.nodeName === 'IDS-MODAL-BUTTON') modal?.hide();
    });

    // Toggle disable elements in hyperlink modal
    if (key === 'hyperlink') {
      const elems = this.#modals[key].elems;
      const removeElem = elems.removeElem;
      const elemsToDisable = Object.entries(elems)
        .filter(([k]) => (!(/^(removeElem|removeContainer)$/.test(k)))).map((x) => x[1]);
      this.offEvent(`change.editor-modal-${key}-checkbox-remove`, removeElem);
      this.onEvent(`change.editor-modal-${key}-checkbox-remove`, removeElem, (e: CustomEvent) => {
        elemsToDisable.forEach((elem: any) => {
          if (elem) elem.disabled = e.detail.checked;
        });
      });
    }
  }

  /**
   * Attach Keyboard events
   * @private
   * @returns {object} This API object for chaining
   */
  #attachKeyboardEvents(): object {
    const hasKey = (action: any[], key: string) => (new RegExp(key, 'i')).test(action[1].keyid);
    const getKey = (action: any[]) => action[1].keyid.replace(/\|.*$/g, '');
    const actions = Object.entries(this.#actions).filter(([, v]) => (v as any).keyid);
    const keys = [...new Set(actions.map((action) => getKey(action)))];
    const mapped: any = {};
    keys.forEach((key: string) => {
      mapped[key] = actions.filter((action) => getKey(action) === key);
    });

    this.onEvent('keydown.editor-container', this, (e: KeyboardEvent) => {
      if (this.disabled || this.readonly) {
        return;
      }
      const key = e.code;
      if (keys.indexOf(key) > -1 && (e.ctrlKey || e.metaKey)) {
        const action = mapped[key]?.filter((a: any) => (
          e.shiftKey === hasKey(a, 'shift') && e.altKey === hasKey(a, 'alt')
        )).flat();

        if (action?.length) {
          if (this.view === 'source' && action[0] !== 'editormode') {
            return;
          }
          if (['hyperlink', 'insertimage'].includes(action[0])) {
            this.#modals[action[0]]?.modal?.show();
          } else {
            this.#savedSelection = saveSelectionObject(window.getSelection()!);
            this.#handleAction(action[0]);
            this.#triggerEvent('input', this.#elems.editor);
          }
          e.preventDefault();
        }
      }
    });
    return this;
  }

  /**
   * Set editor html value
   * @param {string | null | undefined} value Html Text string
   */
  set value(value: string | null | undefined) {
    const content = value ?? '';
    this.#editorTextContainer!.innerHTML = `${content}`;
    this.#adjustFirstParagraph();
    this.#applySelectionTextColor(this.#editorTextContainer?.parentElement?.parentElement || null);
  }

  /**
   * Get editor current html value
   * @returns {string} the current value
   */
  get value(): string {
    return trimContent?.(this.#editorTextContainer!.innerHTML);
  }

  /**
   * Sets the editor to disabled
   * @param {boolean | string} value If true will set disabled
   */
  set disabled(value: boolean | string) {
    const isDisabled = stringToBool(value);
    this.toggleAttribute(attributes.DISABLED, isDisabled);
    this.container?.toggleAttribute(attributes.DISABLED, isDisabled);
    if (this.#elems?.textarea) this.#elems.textarea.toggleAttribute(attributes.DISABLED, isDisabled);
    this.labelEl?.toggleAttribute(attributes.DISABLED, isDisabled);
    this.#contenteditable();
    this.#disabledHyperlinks();
  }

  /**
   * Get the disabled state
   * @returns {boolean | string} true if disabled
   */
  get disabled(): boolean | string {
    const value = this.getAttribute(attributes.DISABLED);
    return value !== null ? stringToBool(value) : EDITOR_DEFAULTS.disabled;
  }

  /**
   * Used for setting the text contents of the shadowroot label
   * @param {string} [value] of label
   * @returns {void}
   */
  setLabelText(value = this.state?.label): void {
    const labelEl = this.labelEl ?? qs('#editor-label', this.shadowRoot);
    const sourceLabel = qs('[for="source-textarea"]', this.shadowRoot);
    const shouldDisplayLabel = this.labelState === null;

    if (labelEl) labelEl.innerHTML = shouldDisplayLabel ? value : '';
    if (sourceLabel) sourceLabel.innerHTML = shouldDisplayLabel ? this.sourceTextareaLabel() : '';

    // Keep the editable region's accessible name in sync when the label is hidden/collapsed
    this.#syncAriaLabel();
  }

  /**
   * Set the paragraph separator for editor
   * @param {string} value The value
   */
  set paragraphSeparator(value: string) {
    if (PARAGRAPH_SEPARATORS.indexOf(value) > -1) {
      this.setAttribute(attributes.PARAGRAPH_SEPARATOR, value);
    } else {
      this.removeAttribute(attributes.PARAGRAPH_SEPARATOR);
    }
    this.#setParagraphSeparator?.();
  }

  /**
   * Get the paragraph separator
   * @returns {string} the paragraph separator
   */
  get paragraphSeparator() {
    return this.getAttribute(attributes.PARAGRAPH_SEPARATOR) || EDITOR_DEFAULTS.paragraphSeparator;
  }

  /**
   * Sets to be paste as plain text for editor
   * @param {boolean} value The value
   */
  set pasteAsPlainText(value: boolean) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.PASTE_AS_PLAIN_TEXT, '');
    } else {
      this.removeAttribute(attributes.PASTE_AS_PLAIN_TEXT);
    }
  }

  /**
   * Get the paste as plain text setting
   * @returns {boolean} true if paste as plain text is enabled
   */
  get pasteAsPlainText() {
    const value = this.getAttribute(attributes.PASTE_AS_PLAIN_TEXT);
    return value !== null ? stringToBool(value) : EDITOR_DEFAULTS.pasteAsPlainText;
  }

  /**
   * Set the placeholder text for editor
   * @param {string} value The placeholder value
   */
  set placeholder(value: string | null) {
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, value);
      if (this.#elems?.editor) this.#elems.editor.setAttribute(attributes.PLACEHOLDER, value);
      if (this.#elems?.textarea) this.#elems.textarea.setAttribute(attributes.PLACEHOLDER, value);
    } else {
      this.removeAttribute(attributes.PLACEHOLDER);
      if (this.#elems?.editor) this.#elems.editor.removeAttribute(attributes.PLACEHOLDER);
      if (this.#elems?.textarea) this.#elems.textarea.removeAttribute(attributes.PLACEHOLDER);
    }
  }

  /**
   * Get the placeholder text
   * @returns {string | null} the placeholder text
   */
  get placeholder() {
    return this.getAttribute(attributes.PLACEHOLDER);
  }

  /**
   * Sets the editor to readonly
   * @param {boolean | string} value If true will set readonly
   */
  set readonly(value: boolean | string) {
    const isReadOnly = stringToBool(value);
    this.toggleAttribute(attributes.READONLY, isReadOnly);
    this.container?.toggleAttribute(attributes.READONLY, isReadOnly);
    if (this.#elems?.textarea) this.#elems.textarea.toggleAttribute(attributes.READONLY, isReadOnly);
    this.labelEl?.toggleAttribute(attributes.READONLY, isReadOnly);
    this.#contenteditable();
  }

  /**
   * Get the readonly state
   * @returns {boolean | string} true if readonly
   */
  get readonly(): boolean | string {
    const value = this.getAttribute(attributes.READONLY);
    return value !== null ? stringToBool(value) : EDITOR_DEFAULTS.readonly;
  }

  /**
   * Sets to be use source formatter for editor
   * @param {boolean} value The value
   */
  set sourceFormatter(value: boolean) {
    this.toggleAttribute(attributes.SOURCE_FORMATTER, stringToBool(value));
  }

  /**
   * Get the source formatter setting
   * @returns {boolean} true if source formatter is enabled
   */
  get sourceFormatter() {
    const value = this.getAttribute(attributes.SOURCE_FORMATTER);
    return value !== null ? stringToBool(value) : EDITOR_DEFAULTS.sourceFormatter;
  }

  /**
   * Set the view mode for editor
   * @param {string} value The value: 'editor', 'source'
   */
  set view(value: string) {
    if (VIEWS.includes(value)) {
      const attr = this.getAttribute(attributes.VIEW);
      let veto = null;

      if (this.view !== value) {
        veto = /source/i.test(value) ? this.#sourceMode() : this.#editorMode();
      }

      if (veto || (veto === null && attr !== value)) {
        this.setAttribute(attributes.VIEW, value);
      }

      if (veto) {
        this.#triggerEvent(`after${value}mode`);
        this.#triggerEvent('viewchange', this, { view: value });
      }
    } else {
      this.removeAttribute(attributes.VIEW);
    }
  }

  /**
   * Get the view mode
   * @returns {string} the view mode ('editor' or 'source')
   */
  get view(): string {
    return this.getAttribute(attributes.VIEW) || EDITOR_DEFAULTS.view;
  }

  /**
   * Set the height of the editor
   * @param {string} value The height value (e.g., '300px', '50vh')
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
      this.shadowRoot?.querySelector<HTMLElement>('.main-container')?.style.setProperty('height', value);
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.shadowRoot?.querySelector<HTMLElement>('.main-container')?.style.removeProperty('height');
    }
  }

  /**
   * Get the height of the editor
   * @returns {string | null} the height value
   */
  get height(): string | null {
    return this.getAttribute(attributes.HEIGHT);
  }

  /**
   * Get the editor slot element
   * @returns {HTMLSlotElement} the editor slot
   */
  get editorSlot(): HTMLSlotElement {
    return this.container!.querySelector('slot#editor-slot')!;
  }

  /**
   * Get the hidden slot element
   * @returns {HTMLSlotElement} the hidden slot
   */
  get hiddenSlot(): HTMLSlotElement {
    return this.container!.querySelector('slot#hidden-slot')!;
  }

  /**
   * Handles editor mode changes and updates toolbar item states
   * @param {string} actionName - The mode action name to switch to
   */
  handleChangeEditorMode(actionName: string) {
    this.view = actionName.replace(/mode/i, '');
    const elem = this.#elems;
    elem.toolbar.querySelectorAll('ids-toolbar-more-actions ids-popup-menu ids-menu-group ids-menu-item').forEach((element: HTMLElement) => {
      const icon = element?.getAttribute(attributes.ICON);
      if (!icon || ['html', 'visual'].includes(icon)) {
        return;
      }
      element.toggleAttribute(attributes.DISABLED, this.view === 'source');
    });
  }
}
